/**
 * DuckDB-WASM engine — initializes DuckDB in the browser for spatial SQL queries.
 * Loads spatial extension and provides query/spatialQuery functions.
 *
 * LAZY INIT (CL-140, BUG-050): nothing downloads until init() is called. The
 * ~33 MB WASM binaries are fetched on first analytic need (the cold-start gate
 * in main.js awaits ensureDuckDB()/waitReady()) or by the background prewarm
 * scheduler — whichever comes first. init() is promise-memoized, so every
 * consumer (query, waitReady, ensureDuckDB, boundary-feature-service) joins
 * the same in-flight instantiation; a FAILED init clears the memo so the next
 * demand can retry instead of poisoning the whole session.
 */
import * as duckdb from '@duckdb/duckdb-wasm';
import { R2_PARQUET_URL, TIMEOUT_DUCKDB_QUERY } from '../config.js';

class DuckDBEngine {
  constructor() {
    this.db = null;
    this.conn = null;
    this.isReady = false;
    this.isLoading = false;
    this._readyPromise = null;
    this.onStatusChange = null;
  }

  /**
   * Initialize DuckDB-WASM asynchronously — downloads ~33MB of WASM binaries.
   * Promise-memoized: concurrent callers share one instantiation. Do NOT call
   * eagerly at page load (BUG-050); let ensureDuckDB()/the prewarm scheduler
   * decide when.
   */
  async init() {
    if (this.isReady) return;
    if (this._readyPromise) return this._readyPromise;

    this.isLoading = true;
    this.onStatusChange?.('loading');

    // Clear the memo on failure so the NEXT demand retries the download
    // instead of every future query failing against a stale rejection.
    this._readyPromise = this._doInit().catch((err) => {
      this._readyPromise = null;
      throw err;
    });
    return this._readyPromise;
  }

  async _doInit() {
    try {
      console.log('[DuckDB] Selecting bundle...');
      // Use jsDelivr CDN bundles — most reliable with Vite
      const bundles = duckdb.getJsDelivrBundles();
      const best = await duckdb.selectBundle(bundles);

      console.log('[DuckDB] Instantiating worker...');
      const worker = await duckdb.createWorker(best.mainWorker);
      const logger = new duckdb.ConsoleLogger();

      this.db = new duckdb.AsyncDuckDB(logger, worker);
      await this.db.instantiate(best.mainModule, best.pthreadWorker);

      console.log('[DuckDB] Opening connection...');
      this.conn = await this.db.connect();

      console.log('[DuckDB] Installing spatial extension...');
      await this.conn.query('INSTALL spatial;');
      await this.conn.query('LOAD spatial;');

      this.isReady = true;
      this.isLoading = false;
      this.onStatusChange?.('ready');
      console.log('[DuckDB] Ready with spatial extension');

      // Preload small aggregation tables for instant analytical queries
      this._preloadAggregations();

    } catch (err) {
      this.isLoading = false;
      this.onStatusChange?.('error');
      console.error('[DuckDB] Initialization failed:', err);
      throw err;
    }
  }

  /**
   * Lazily install + load the h3 community extension.
   * Verified working in DuckDB-WASM (incl. from UAE networks, ~700ms install,
   * 2026-06-12). Never throws — returns false on failure so callers can fall
   * back to the unsmoothed path. Result is cached for the session.
   *
   * @returns {Promise<boolean>} true when h3 functions are available
   */
  async ensureH3() {
    if (this._h3Ready != null) return this._h3Ready;
    if (this._h3Promise) return this._h3Promise;
    this._h3Promise = (async () => {
      try {
        await this.waitReady();
        await this.conn.query('INSTALL h3 FROM community;');
        await this.conn.query('LOAD h3;');
        this._h3Ready = true;
        console.log('[DuckDB] h3 community extension loaded');
      } catch (err) {
        this._h3Ready = false;
        console.warn('[DuckDB] h3 extension unavailable:', err.message);
      }
      return this._h3Ready;
    })();
    return this._h3Promise;
  }

  /**
   * Wait until DuckDB is ready. If not initialized, starts init (lazy,
   * CL-140) and joins the memoized init promise — rejects on init failure
   * rather than hanging, so callers can fall back.
   */
  async waitReady() {
    if (this.isReady) return this;
    await this.init();
    return this;
  }

  /**
   * Execute a SQL query and return results as array of objects.
   * @param {string} sql - DuckDB SQL query
   * @param {number} timeout - Timeout in ms (default 15s)
   * @returns {Promise<object[]>} Array of row objects
   */
  async query(sql, timeout = TIMEOUT_DUCKDB_QUERY) {
    await this.waitReady();

    return Promise.race([
      this._executeQuery(sql),
      new Promise((_resolve, reject) =>
        setTimeout(() => reject(new Error('Query timeout (15s). Try narrowing your request to a specific region.')), timeout)
      ),
    ]);
  }

  async _executeQuery(sql) {
    try {
      const result = await this.conn.query(sql);
      return this._arrowToObjects(result);
    } catch (err) {
      console.error('[DuckDB] Query failed:', sql);
      console.error('[DuckDB] Error:', err.message);
      throw err;
    }
  }

  /**
   * Execute a spatial SQL query and return results as a GeoJSON FeatureCollection.
   * The SQL must include a `geojson` column (via ST_AsGeoJSON(geometry)).
   * @param {string} sql - DuckDB SQL query with geojson column
   * @returns {Promise<object>} GeoJSON FeatureCollection
   */
  async spatialQuery(sql) {
    const rows = await this.query(sql);
    return this._rowsToGeoJSON(rows);
  }

  /**
   * Convert Arrow table result to array of plain objects.
   */
  _arrowToObjects(arrowResult) {
    const rows = [];
    const schema = arrowResult.schema.fields.map(f => f.name);

    for (let i = 0; i < arrowResult.numRows; i++) {
      const row = {};
      for (const field of schema) {
        const col = arrowResult.getChild(field);
        let val = col?.get(i);
        // Convert BigInt to Number for JSON compatibility
        if (typeof val === 'bigint') {
          val = Number(val);
        }
        row[field] = val;
      }
      rows.push(row);
    }
    return rows;
  }

  /**
   * Convert row objects to GeoJSON FeatureCollection.
   * Expects a `geojson` column with GeoJSON geometry strings.
   */
  _rowsToGeoJSON(rows) {
    const features = [];

    for (const row of rows) {
      const geojsonStr = row.geojson;
      if (!geojsonStr) continue;

      let geometry;
      try {
        geometry = JSON.parse(geojsonStr);
      } catch {
        continue;
      }

      // Build properties from all non-geometry columns
      const properties = {};
      for (const [key, value] of Object.entries(row)) {
        if (key !== 'geojson' && key !== 'geometry') {
          properties[key] = value;
        }
      }

      features.push({
        type: 'Feature',
        geometry,
        properties,
      });
    }

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  /**
   * Preload small aggregation tables into DuckDB as local views.
   * This way queries against these tables don't need HTTP range requests.
   */
  async _preloadAggregations() {
    const r2Url = R2_PARQUET_URL;
    const tables = [
      { name: 'country_summary', path: 'data/country_summary.parquet' },
      { name: 'continent_summary', path: 'data/continent_summary.parquet' },
      { name: 'temporal_population', path: 'data/temporal_population.parquet' },
      { name: 'country_indicators_latest', path: 'data/country_indicators_latest.parquet' },
    ];

    // Cache-bust R2 CDN to ensure fresh data after uploads
    const cacheBuster = `v=${Date.now()}`;
    for (const { name, path } of tables) {
      try {
        const url = `${r2Url}/${path}?${cacheBuster}`;
        await this.conn.query(`CREATE TABLE ${name} AS SELECT * FROM '${url}'`);
        const count = await this.conn.query(`SELECT COUNT(*) as c FROM ${name}`);
        const rows = count.toArray()[0]?.c ?? 0;
        console.log(`[DuckDB] Preloaded ${name}: ${rows} rows`);
      } catch (err) {
        console.warn(`[DuckDB] Failed to preload ${name}:`, err.message);
      }
    }
  }

  /**
   * Clean up DuckDB resources.
   */
  async close() {
    if (this.conn) {
      await this.conn.close();
      this.conn = null;
    }
    if (this.db) {
      await this.db.terminate();
      this.db = null;
    }
    this.isReady = false;
  }
}

// Singleton instance
export const duckdbEngine = new DuckDBEngine();

/**
 * The single lazy-init seam (CL-140): start (or join) DuckDB initialization
 * and resolve with the ready engine. Promise-memoized via init(), so the
 * prewarm scheduler, the cold-start gate, and any implicit consumer racing
 * each other all share one instantiation — never a double download.
 *
 * @returns {Promise<DuckDBEngine>}
 */
export function ensureDuckDB() {
  return duckdbEngine.init().then(() => duckdbEngine);
}

export default duckdbEngine;
